import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Shield, Lock, Eye, FileText, Database, Users } from 'lucide-react';
import { DottedPattern } from '../components/DecorativeElements';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const sections = [
    {
      icon: <FileText size={32} />,
      title: 'Information We Collect',
      content: [
        'Personal Information: Name, email address, phone number, and company details when you contact us or use our services.',
        'Usage Data: Information about how you interact with our website, including pages visited, time spent, and browser type.',
        'Cookies: We use cookies to enhance your browsing experience and analyze website traffic.',
        'Communication Data: Records of correspondence when you contact us via email, phone, or contact forms.',
      ],
    },
    {
      icon: <Eye size={32} />,
      title: 'How We Use Your Information',
      content: [
        'To provide and improve our services and respond to your inquiries.',
        'To send you updates, newsletters, and marketing communications (with your consent).',
        'To analyze website usage and improve user experience.',
        'To comply with legal obligations and protect our rights.',
        'To personalize your experience and deliver relevant content.',
      ],
    },
    {
      icon: <Lock size={32} />,
      title: 'Data Security',
      content: [
        'We implement industry-standard security measures to protect your personal information.',
        'We use encryption, secure servers, and access controls to safeguard your data.',
        'However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.',
        'We regularly review and update our security practices to address emerging threats.',
      ],
    },
    {
      icon: <Database size={32} />,
      title: 'Data Sharing and Disclosure',
      content: [
        'We do not sell, trade, or rent your personal information to third parties.',
        'We may share data with trusted service providers who assist in operating our website and conducting business.',
        'We may disclose information if required by law or to protect our rights and safety.',
        'In case of business transfers, your data may be transferred to the new entity.',
      ],
    },
    {
      icon: <Users size={32} />,
      title: 'Your Rights',
      content: [
        'Right to Access: You can request a copy of the personal data we hold about you.',
        'Right to Rectification: You can request correction of inaccurate or incomplete data.',
        'Right to Erasure: You can request deletion of your personal data under certain circumstances.',
        'Right to Object: You can object to processing of your personal data for marketing purposes.',
        'Right to Data Portability: You can request transfer of your data to another service provider.',
        'Right to Withdraw Consent: You can withdraw consent for data processing at any time.',
      ],
    },
    {
      icon: <Shield size={32} />,
      title: 'Cookies Policy',
      content: [
        'We use essential cookies necessary for website functionality.',
        'We use analytics cookies to understand how visitors interact with our website.',
        'We use marketing cookies (with consent) to deliver personalized advertisements.',
        'You can manage cookie preferences through your browser settings.',
        'Disabling cookies may affect website functionality.',
      ],
    },
  ];

  return (
    <div className="privacy-policy-page">
      <section className="privacy-hero">
        <DottedPattern />
        <div className="container">
          <motion.h1
            className="page-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Privacy Policy
          </motion.h1>
          <motion.p
            className="page-subtitle"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </motion.p>
        </div>
      </section>

      <section className="privacy-content" ref={ref}>
        <div className="container">
          <motion.div
            className="privacy-intro"
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <p>
              At Team FullStack, we are committed to protecting your privacy and ensuring the security of your personal information. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website 
              or use our services.
            </p>
            <p>
              By using our website, you agree to the collection and use of information in accordance with this policy. 
              If you do not agree with our policies and practices, please do not use our services.
            </p>
          </motion.div>

          {sections.map((section, index) => (
            <motion.div
              key={index}
              className="privacy-section"
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 + index * 0.1 }}
            >
              <div className="privacy-section-header">
                <div className="privacy-section-icon">{section.icon}</div>
                <h2>{section.title}</h2>
              </div>
              <ul className="privacy-section-content">
                {section.content.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </motion.div>
          ))}

          <motion.div
            className="privacy-contact"
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            <h2>Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy or wish to exercise your rights, please contact us:
            </p>
            <div className="privacy-contact-info">
              <p><strong>Email:</strong> <a href="mailto:parth@teamfullstack.in">parth@teamfullstack.in</a></p>
              <p><strong>Phone:</strong> <a href="tel:+917668628401">+91 76686 28401</a></p>
              <p><strong>Address:</strong> Unit G22, Kohinoor City Mall, Kohinoor City, Kurla (West) 400070 Mumbai</p>
            </div>
          </motion.div>

          <motion.div
            className="privacy-updates"
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 1 }}
          >
            <h2>Policy Updates</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new 
              Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy 
              periodically for any changes.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;

